import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import CircularProgress from '@material-ui/core/CircularProgress';
import Icon from '@material-ui/core/Icon';

// Feature CSAT Viagem (2026-08-28): avaliação pós-serviço enviada por e-mail
// D+3 após o último serviço do cliente (tour ou transfer). NÃO confundir com
// a "Satisfação (CSAT)" de tickets de suporte (pages/tickets/Csat.jsx) — nome
// escolhido explicitamente pelo Marcelo pra diferenciar as duas.
// Restrita a administrador/gerente/logística (mesma trava do backend).

const EMOJIS = { 1: '😠', 2: '😕', 3: '😐', 4: '🙂', 5: '😄' };
const RATING_COLORS = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#84cc16', 5: '#22c55e' };

const avgColor = (avg) => {
  if (!avg) return '#94a3b8';
  if (avg >= 4) return '#22c55e';
  if (avg >= 3) return '#eab308';
  return '#ef4444';
};

const FilterBtn = ({ active, onClick, color, children }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-lg text-sm transition-all"
    style={{
      background: active ? color + '33' : '#0f2340',
      border: `1px solid ${active ? color : '#1e3a5f'}`,
      color: active ? '#fff' : '#94a3b8',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

const Section = ({ title, children, className = '' }) => (
  <div className={`p-5 rounded-xl ${className}`} style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}>
    {title && <h2 className="text-sm font-semibold mb-4" style={{ color: '#94a3b8' }}>{title}</h2>}
    {children}
  </div>
);

const KpiCard = ({ label, value, color }) => (
  <div className="p-4 rounded-xl" style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}>
    <p className="text-xs mb-1" style={{ color: '#64748b' }}>{label}</p>
    <p className="text-2xl font-bold" style={{ color: color || '#fff' }}>{value}</p>
  </div>
);

const CsatViagem = (props) => {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [sellerFilter, setSellerFilter] = useState(null);
  const [onlyLowPending, setOnlyLowPending] = useState(false);
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteError, setNoteError] = useState({});
  const [noteSaved, setNoteSaved] = useState({});
  const [onlyLowResolved, setOnlyLowResolved] = useState(false);

  useEffect(() => { props.setShowMenu(false); }, []);

  const fetchData = () => {
    setLoading(true);
    apiClient.httpInstance
      .get('/evaluations', {
        params: {
          seller_user_id: sellerFilter,
          pageSize: 100,
        },
      })
      .then((res) => { if (res.status === 200) setResult(res.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerFilter]);

  const markRead = (id) => {
    apiClient.httpInstance.put(`/evaluations/${id}/read`).then(() => fetchData());
  };

  const saveNote = (id) => {
    setSavingNoteId(id);
    setNoteError((prev) => ({ ...prev, [id]: null }));
    setNoteSaved((prev) => ({ ...prev, [id]: false }));
    apiClient.httpInstance
      .put(`/evaluations/${id}/note`, { internal_note: noteDrafts[id] ?? '' })
      .then(() => {
        fetchData();
        setNoteSaved((prev) => ({ ...prev, [id]: true }));
        setTimeout(() => setNoteSaved((prev) => ({ ...prev, [id]: false })), 3000);
      })
      .catch((err) => {
        setNoteError((prev) => ({
          ...prev,
          [id]: err?.response?.data?.message || 'Não foi possível salvar a observação. Tente novamente.',
        }));
      })
      .finally(() => setSavingNoteId(null));
  };

  const rows = result?.data || [];
  const visibleRows = onlyLowPending
    ? rows.filter((r) => r.rating <= 3 && !r.read_at)
    : onlyLowResolved
      ? rows.filter((r) => r.rating <= 3 && r.read_at)
      : rows;

  return (
    <div className="px-7 pb-10 max-w-6xl">
      {/* ── Cabeçalho ── */}
      <div className="mt-2 mb-5">
        <h1 className="text-2xl font-bold text-white">
          ⭐ CSAT Viagem
        </h1>
        <p className="text-sm mt-1" style={{ color: '#64748b' }}>
          Avaliações enviadas por e-mail 3 dias após o último serviço do cliente (tour ou transfer).
        </p>
      </div>

      {loading && !result ? (
        <div className="flex justify-center py-24">
          <CircularProgress style={{ color: props.theme?.color || '#62DEC2' }} />
        </div>
      ) : (
        <>
          {/* ── KPIs ── */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 mb-5">
            <KpiCard
              label="Média geral"
              value={result?.overall_avg ? `${result.overall_avg} ${EMOJIS[Math.round(result.overall_avg)] || ''}` : '—'}
              color={avgColor(result?.overall_avg)}
            />
            <KpiCard label="Avaliações respondidas" value={rows.length} />
            <KpiCard
              label="Notas baixas pendentes"
              value={result?.low_rating_pending ?? 0}
              color={result?.low_rating_pending > 0 ? '#ef4444' : '#22c55e'}
            />
            <KpiCard
              label="Notas baixas concluídas"
              value={result?.low_rating_resolved ?? 0}
              color="#22c55e"
            />
            {/* Enviados hoje/total (2026-09-16) — pra confirmar de relance, sem
                precisar checar o banco, que a régua diária de convites continua
                rodando (útil pra distinguir "parou de enviar" de "ninguém
                respondeu ainda"). */}
            <KpiCard
              label="Convites enviados hoje"
              value={result?.sent_today ?? 0}
              color={result?.sent_today > 0 ? '#62DEC2' : '#94a3b8'}
            />
            <KpiCard label="Convites enviados (total)" value={result?.sent_total ?? 0} />
          </div>

          {/* ── Média por consultor ── */}
          <Section title="Média por consultor" className="mb-5">
            <div className="flex flex-wrap gap-2">
              <FilterBtn active={!sellerFilter} onClick={() => setSellerFilter(null)} color="#62DEC2">
                Geral
              </FilterBtn>
              {(result?.per_seller || []).map((s) => (
                <FilterBtn
                  key={s.seller_user_id}
                  active={sellerFilter === s.seller_user_id}
                  onClick={() => setSellerFilter(s.seller_user_id)}
                  color={avgColor(Number(s.avg_rating))}
                >
                  {s.seller_name} — {Number(s.avg_rating).toFixed(2)} ({s.total})
                </FilterBtn>
              ))}
              {(!result?.per_seller || result.per_seller.length === 0) && (
                <p className="text-sm" style={{ color: '#64748b' }}>Nenhuma avaliação respondida ainda.</p>
              )}
            </div>
          </Section>

          {/* ── Régua nota baixa: pendente / concluída ── */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <FilterBtn
              active={onlyLowPending}
              onClick={() => { setOnlyLowPending(!onlyLowPending); setOnlyLowResolved(false); }}
              color="#ef4444"
            >
              🔔 Só notas baixas pendentes de leitura
            </FilterBtn>
            <FilterBtn
              active={onlyLowResolved}
              onClick={() => { setOnlyLowResolved(!onlyLowResolved); setOnlyLowPending(false); }}
              color="#22c55e"
            >
              ✅ Só notas baixas concluídas
            </FilterBtn>
          </div>

          {/* ── Lista ── */}
          {visibleRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-xl" style={{ background: '#0f2340', border: '1px solid #1e3a5f' }}>
              <span style={{ fontSize: 40 }}>⭐</span>
              <p className="mt-3 text-sm" style={{ color: '#64748b' }}>Nenhuma avaliação encontrada com esse filtro.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {visibleRows.map((row) => {
                const isLow = row.rating <= 3;
                const unread = isLow && !row.read_at;
                return (
                  <div
                    key={row.id}
                    className="p-4 rounded-xl"
                    style={{
                      background: '#0f2340',
                      border: `1px solid ${unread ? '#ef4444' : '#1e3a5f'}`,
                    }}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <span style={{ fontSize: 28, color: RATING_COLORS[row.rating] }}>
                          {EMOJIS[row.rating]} {row.rating}/5
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-white">{row.customer?.name || 'Cliente'}</p>
                          <p className="text-xs" style={{ color: '#64748b' }}>
                            {row.sale?.references ? `${row.sale.references} · ` : ''}
                            Consultor: {row.seller?.name || '—'}
                          </p>
                        </div>
                      </div>
                      {unread && (
                        <button
                          type="button"
                          onClick={() => markRead(row.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs"
                          style={{ background: '#ef444433', border: '1px solid #ef4444', color: '#fff', cursor: 'pointer' }}
                        >
                          <Icon style={{ fontSize: 16 }}>visibility</Icon> Marcar como lido
                        </button>
                      )}
                      {isLow && row.read_at && (
                        <span className="text-xs" style={{ color: '#22c55e' }}>✓ Lido</span>
                      )}
                    </div>

                    {row.comment && (
                      <p className="text-sm mt-3" style={{ color: '#cbd5e1' }}>“{row.comment}”</p>
                    )}

                    {isLow && (
                      <div className="mt-3">
                        <textarea
                          value={noteDrafts[row.id] ?? row.internal_note ?? ''}
                          onChange={(e) => setNoteDrafts({ ...noteDrafts, [row.id]: e.target.value })}
                          placeholder="Observação interna do time (não visível pro cliente)"
                          rows={2}
                          className="w-full text-sm rounded-lg p-2"
                          style={{ background: '#0a1628', border: '1px solid #1e3a5f', color: '#dce9fb', resize: 'vertical' }}
                        />
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => saveNote(row.id)}
                            disabled={savingNoteId === row.id}
                            className="px-3 py-1.5 rounded-lg text-xs"
                            style={{ background: '#62DEC233', border: '1px solid #62DEC2', color: '#fff', cursor: savingNoteId === row.id ? 'default' : 'pointer' }}
                          >
                            {savingNoteId === row.id ? 'Salvando...' : 'Salvar observação'}
                          </button>
                          {noteSaved[row.id] && (
                            <span className="text-xs" style={{ color: '#22c55e' }}>✓ Salvo</span>
                          )}
                          {noteError[row.id] && (
                            <span className="text-xs" style={{ color: '#ef4444' }}>{noteError[row.id]}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CsatViagem;
